/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * FRONTEND - AFFILIATE SERVICE
 * UPDATED: 2026-03-25
 * FIXES: [1] PUT→PATCH [2] /students?affiliateId= [3] POST /students com body
 *        [4] stats via GET /affiliates/:id [5] getMyAffiliate() via JWT
 */

import { apiClient } from './api';

export interface Affiliate {
  id: string; userId?: string; systemId: string; schoolName: string;
  responsibleName?: string; // ✅ NOVO — nome do responsável cadastrado
  description?: string; address?: string; city: string; state: string;
  postalCode?: string; phone?: string; whatsapp?: string; website?: string;
  logoUrl?: string; email?: string; cpf?: string; cnpj?: string;
  status: 'pending' | 'active' | 'inactive' | 'suspended';
  renewalDate?: string; studentCount: number; courseCount: number;
  createdAt: string; updatedAt: string;
}

export interface AffiliateStudent {
  id: string; systemId: string; fullName: string; email?: string;
  cpf?: string; phone?: string; photoUrl?: string;
  status: 'active' | 'graduated' | 'suspended' | 'inactive';
  evasionFlag: boolean; enrollmentDate: string; createdAt: string;
}

export interface AffiliateStats {
  totalStudents: number; activeStudents: number;
  totalCourses: number; activeCourses: number;
  renewalStatus: 'active' | 'pending' | 'overdue'; renewalDate?: string;
}

export interface RegisterStudentRequest {
  affiliateId: string; fullName: string; email?: string; cpf?: string;
  phone?: string; birthdate?: string; address?: string;
  city?: string; state?: string; postalCode?: string; enrollmentDate?: string;
}

class AffiliateService {

  async getMyAffiliate(): Promise<Affiliate> {
    return apiClient.get<Affiliate>('/affiliates/me');
  }

  async getAffiliate(id: string): Promise<Affiliate> {
    return apiClient.get<Affiliate>(`/affiliates/${id}`);
  }

  async updateAffiliate(id: string, data: Partial<Affiliate>): Promise<Affiliate> {
    return apiClient.patch<Affiliate>(`/affiliates/${id}`, data);
  }

  async uploadProfilePhoto(id: string, file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', 'profile_photo');
    formData.append('affiliateId', id);
    return apiClient.post<{ url: string }>('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getStudents(
    affiliateId: string,
    params?: { page?: number; limit?: number; status?: string; search?: string },
  ): Promise<{ students: AffiliateStudent[]; total: number }> {
    return apiClient.get<{ students: AffiliateStudent[]; total: number }>(
      '/students', { params: { affiliateId, ...params } },
    );
  }

  async registerStudent(data: RegisterStudentRequest): Promise<AffiliateStudent> {
    return apiClient.post<AffiliateStudent>('/students', data);
  }

  async updateStudent(id: string, data: Partial<RegisterStudentRequest>): Promise<AffiliateStudent> {
    return apiClient.patch<AffiliateStudent>(`/students/${id}`, data);
  }

  async getStats(affiliateId: string): Promise<AffiliateStats> {
    const a = await this.getAffiliate(affiliateId);
    const now = new Date();
    const rd  = a.renewalDate ? new Date(a.renewalDate) : null;
    const diff = rd ? Math.ceil((rd.getTime() - now.getTime()) / 86_400_000) : null;
    const renewalStatus = !rd ? 'pending' : diff! < 0 ? 'overdue' : diff! <= 30 ? 'pending' : 'active';
    return {
      totalStudents: a.studentCount, activeStudents: a.studentCount,
      totalCourses: a.courseCount,  activeCourses: a.courseCount,
      renewalStatus: renewalStatus as any, renewalDate: a.renewalDate,
    };
  }

  async getCourses(affiliateId: string, params?: { page?: number; limit?: number; status?: string }) {
    return apiClient.get('/courses', { params: { affiliateId, ...params } });
  }

  async getDocuments(affiliateId: string) {
    return apiClient.get('/documents', { params: { affiliateId } });
  }

  async initiateRenewal(affiliateId: string): Promise<{ checkoutUrl: string }> {
    return apiClient.post<{ checkoutUrl: string }>(`/affiliates/${affiliateId}/renewal`);
  }

  async searchAffiliates(filters: { query?: string; state?: string; city?: string; page?: number; limit?: number }) {
    return apiClient.get('/affiliates/search', { params: filters });
  }
}

export const affiliateService = new AffiliateService();
export default affiliateService;
